import { PopperSurface } from "--floponents-web/molecules/PopperSurface";
import * as React from "react";
import styled from "styled-components";
import { TextInputProps, TextInput } from "../../atoms/TextInput/TextInput";
import DateSidebar, { DateSidebarProps } from "./components/DateSidebar";
import MonthList from "./components/MonthList";
import TimeSidebar, { tFormat, tInterval } from "./components/TimeSidebar";
import IcoDate from "@floji-internal/mui-icons/CalendarViewMonth";
import { SurfaceActionBar } from "../../atoms/Surface";
import { Button } from "../../atoms/Button";
import { Modify } from "--floponents-web/common/utils";
import { Strings } from '--root/strings/en';

const STRINGS = Strings.dateTime;

const MIN_DATE = new Date("1900-01-01 00:00:00");
const MAX_DATE = new Date("2099-12-31 23:59:59");
const MIN_TIME = new Date(new Date().setHours(0, 0, 0, 0));
const MAX_TIME = new Date(new Date().setHours(23, 59, 59));

type DatePickerWrapperProps = {
    showTime?: boolean
}
const DatePickerWrapper = styled.div<DatePickerWrapperProps>`
    position: relative;
    display: flex;
    flex-direction: row;
    height: 300px;
    min-width: ${props => props.showTime ? 450 : 350}px;
    width: 100%;
    overflow: hidden;
    background: ${props => props.theme.palette.surface};
`;
const SideBarWrapper = styled.div`
    position: relative;
    flex: 1 0 100px;
    :before,
    :after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;            
        height: 27%;
        z-index: 1;
        pointer-events: none;
    }
    :before { 
        top: 0;
        background: linear-gradient(180deg, ${props => props.theme.palette.surface} 10%, transparent 100%);
    }
    :after {
        bottom: 0;
        background: linear-gradient(0deg, ${props => props.theme.palette.surface} 10%, transparent 100%);
    }
`;
const CalendarWrapper = styled.div`
    height: 100%;
    flex: 1 0 250px;
    overflow: hidden;
    >* { height: 100%; }
`;

type DatePickerProps = {
    min?: Date
    max?: Date
    date?: Date
    showTime?: boolean
    timeInterval?: tInterval
    timeFormat?: tFormat
    minTime?: Date
    maxTime?: Date
    onPick?: (date?: Date, type?: "time" | "date") => void
}
const DatePicker: React.FC<DatePickerProps> = (props) => {
    const {
        onPick, date, showTime,
        timeInterval = 15, timeFormat = 12,
        min = MIN_DATE, max = MAX_DATE,
        minTime = MIN_TIME, maxTime = MAX_TIME
    } = props;

    const [sidebarType, setSidebarType] = React.useState<DateSidebarProps["type"]>("month");
    const [sidebarActiveDate, setSidebarActiveDate] = React.useState(new Date());
    const [calendarActiveDate, setCalendarActiveDate] = React.useState(new Date());
    const [selectedDate, setSelectedDate] = React.useState(date || new Date());

    const onUserScroll = (date: Date) => {
        setSidebarActiveDate(date);
    }
    const onMonthClick = (date: Date) => {
        console.log("cDebug", date);
        setCalendarActiveDate(date);
        setSidebarActiveDate(date);
        setSidebarType("month");
    }
    const setDate = (date: Date) => {
        const newDate = new Date(selectedDate);
        newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
        setSelectedDate(newDate);
        onPick && onPick(newDate, "date");
    }
    const setTime = (date: Date) => {
        const newDate = new Date(selectedDate);
        newDate.setHours(date.getHours(), date.getMinutes(), 0, 0);
        setSelectedDate(newDate);
        onPick && onPick(newDate, "time");
    }

    return <DatePickerWrapper showTime={showTime}>
        <SideBarWrapper>
            <DateSidebar
                type={sidebarType}
                min={min}
                max={max}
                active={sidebarActiveDate}
                onClick={onMonthClick} />
        </SideBarWrapper>
        <CalendarWrapper>
            <MonthList
                min={min}
                max={max}
                active={calendarActiveDate}
                selected={selectedDate}
                onUserScroll={onUserScroll}
                onClick={setDate}
                onMonthClick={() => setSidebarType("year")} />
        </CalendarWrapper>
        {showTime &&
            <SideBarWrapper>
                <TimeSidebar
                    min={minTime}
                    max={maxTime}
                    active={selectedDate}
                    interval={timeInterval}
                    format={timeFormat}
                    onClick={setTime} />
            </SideBarWrapper>
        }
    </DatePickerWrapper>

}

type DatePickerInputProps = Modify<TextInputProps, DatePickerProps>;
const DatePickerInput: React.FC<DatePickerInputProps> = (props) => {
    const { date, showTime, onPick, min, max, ...inputProps } = props;

    const popperTargetRef = React.useRef<HTMLDivElement>(null);
    const [open, setOpen] = React.useState<boolean>(false);
    const [userDate, setUserDate] = React.useState<Date | undefined>(date);

    const strDate = date && (showTime ? date.toLocaleString() : date.toLocaleDateString());
    const placeholder = showTime ? STRINGS.selectBoth : STRINGS.selectDate;

    const saveDate = () => {
        onPick && onPick(userDate);
        setOpen(false);
    }

    React.useEffect(() => {
        const elem = popperTargetRef.current;
        if (!elem) return;
        const open = (e: Event) => {
            e.stopPropagation();
            setOpen(true);
        }
        elem.addEventListener("click", open);
        elem.addEventListener("focusin", open);
        return () => {
            elem.removeEventListener("focusin", open)
            elem.removeEventListener("click", open)
        }
    }, [])

    return <>
        <div ref={popperTargetRef}>
            <TextInput trailingComponent={<IcoDate />} {...inputProps} type="text" value={strDate || ""} placeholder={placeholder} />
        </div>
        <PopperSurface placement="bottom-start" triggerRef={popperTargetRef} open={open} onClose={() => setOpen(false)}>
            <DatePicker onPick={setUserDate} min={min} max={max} date={date} showTime={showTime} />
            <SurfaceActionBar>
                <Button onClick={saveDate}>Save</Button>
            </SurfaceActionBar>
        </PopperSurface>
    </>
}

export default DatePickerInput;
export {
    DatePicker, DatePickerProps,
    DatePickerInput, DatePickerInputProps
};